<template>
  <div class="create-project mx-auto max-w-7xl">
    <form
      @submit.prevent="form.handleSubmit()"
    >
      <div class="flex flex-col sm:flex-row gap-6 sm:gap-8 justify-around">
        <div class="project-details basis-7/12">
          <form.Field name="type">
            <template #default="{ field }">
              <BaseSelect
                v-model="(field.state.value as string)"
                :name="field.name"
                :options="projectTypeOptions"
                description="Select project type, default is residential"
                label="Type"
                @blur="field.handleBlur"
                @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <form.Field name="status">
            <template #default="{ field }">
              <BaseSelect
                v-model="(field.state.value as string)"
                :name="field.name"
                :options="projectStatusOptions"
                description="Select project status, default is estimating"
                label="Status"
                class="mt-4"
                @blur="field.handleBlur"
                @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="name"
            :validators="{
              onBlur: yup.string().required('Project name is required')
            }"
          >
            <template #default="{ field }">
              <BaseInput
                v-model="(field.state.value as string)"
                :name="field.name"
                description="Enter name for this project"
                label="Project Name"
                class="mt-4"
                :error="field.state.meta.errors && field.state.meta.errors[0]"
                @blur="field.handleBlur"
                @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <form.Field name="description">
            <template #default="{ field }">
              <BaseTextArea
                v-model="(field.state.value as string)"
                :name="field.name"
                description="Long description for this project"
                label="Description"
                resizable
                class="mt-4"
                @blur="field.handleBlur"
                @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="customerId"
            :validators="{
              onChange: yup.string().required('A customer is required')
            }"
          >
            <template #default="{ field }">
              <CustomerAutoComplete
                :model-value="field.state.value"
                :name="field.name"
                label="Client"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @update:model-value="(value) => field.handleChange(value)"
              />
            </template>
          </form.Field>

          <form.Field
            name="address"
            :validators="{
              onChange: yup.string().required('Address is required')
            }"
          >
            <template #default="{ field }">
              <AddressAutoComplete
                :name="field.name"
                :value="field.state.value"
                label="Project Location"
                placeholder="Address"
                class="mt-4"
                @blur="field.handleBlur"
                @input="(e) =>{ setAddresse(e); return field.handleChange(e) }"
              />
            </template>
          </form.Field>
        </div>
        <div class="project-admins">
          <fieldset class="mt-6">
            <legend class="text-lg font-semibold">Administrators</legend>
            <form.Field name="projectManagerId">
              <template #default="{ field }">
                <BaseSelect
                  v-model="(field.state.value as string)"
                  :name="field.name"
                  description="Select project manager associated with this project"
                  label="Project Manager"
                  :options="users"
                  class="mt-2"
                  @blur="field.handleBlur"
                  @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
                />
              </template>
            </form.Field>

            <form.Field name="salesPersonId">
              <template #default="{ field }">
                <BaseSelect
                  v-model="(field.state.value as string)"
                  :name="field.name"
                  description="Select salesperson associated with this project"
                  label="Sales Person"
                  :options="users"
                  class="mt-4"
                  @blur="field.handleBlur"
                  @input="(e) => typeof e === 'string' ? field.handleChange(e) : field.handleChange(e.target.value)"
                />
              </template>
            </form.Field>

            <form.Field
              name="realtorId"
            >
              <template #default="{ field }">
                <CustomerAutoComplete
                  :model-value="field.state.value"
                  :name="field.name"
                  label="Realtor"
                  class="mt-4"
                  :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                  @blur="field.handleBlur"
                  @update:model-value="(value) => field.handleChange(value)"
                />
              </template>
            </form.Field>
          </fieldset>
        </div>
      </div>

      <hr class="mt-6">

      <div class="flex justify-end gap-2 mt-3">
        <form.Subscribe>
          <template #default="{ canSubmit, isSubmitting }">
            <BaseButton
              variant="base"
              text="Cancel"
              :to="{ name: 'ListProjects' }"
              :disabled="isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
            />

            <BaseButton
              variant="primary"
              text="Save"
              type="submit"
              :disabled="!canSubmit || isSubmitting"
              :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
            />
          </template>
        </form.Subscribe>
      </div>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { extend, isEmpty } from 'underscore'
import { Project, PROJECT_STATUS } from '@/models/project'
import { ref } from 'vue'
import CustomerService from '@/services/CustomerService'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextArea from '@/components/base/BaseTextArea.vue'
import AddressAutoComplete from '@/components/base/AddressAutoComplete.vue'
import { UserType } from '@/models/user'
import UserService from '@/services/UserService'
import BaseButton from '@/components/base/BaseButton.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import CustomerAutoComplete from '@/components/base/CustomerAutoComplete.vue'

const props = defineProps({
  project: {
    type: Object,
    default: null
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const projectForm = ref({
  type: 'residential',
  status: PROJECT_STATUS.ESTIMATING,
  customerId: '',
  address: ''
} as Project)

const projectStatusOptions = [
  { value: PROJECT_STATUS.IN_PROGRESS, label: 'In Progress' },
  { value: PROJECT_STATUS.COMPLETED, label: 'Completed' },
  { value: PROJECT_STATUS.ARCHIVED, label: 'Archived' },
  { value: PROJECT_STATUS.ESTIMATING, label: 'Estimating', disabled: ((props.project && props.project.id) ?? false) },
  { value: PROJECT_STATUS.AWAITING_ACCEPTANCE, label: 'Awaiting Acceptance', disabled: true },
  { value: PROJECT_STATUS.LOST, label: 'Lost', disabled: true }
]

const projectTypeOptions = [
  { value: 'residential', label: 'Residential'},
  { value: 'commercial', label: 'Commercial'},
  { value: 'pre_sale', label: 'Pre-Sale'},
  { value: 'property_management', label: 'Property Management'},
  { value: 'residential_realtor', label: 'Residential Realtor'},
  { value: 'residential_homeowner', label: 'Residential Homeowner'}
]

const customerService = new CustomerService()
const customerOptions = ref([])
customerService.all().then(customers => customerOptions.value = customers.map(c => ({ value: c.id, label: c.name, address: c.address ?? '' })))

const userService = new UserService()
const users = ref([])
userService.fetchUsers(UserType.EMPLOYEE).then(usrs => users.value = usrs.map(u => ({ value: u.id, label: u.name })))

if (props.project && !isEmpty(props.project)) {
  extend(projectForm.value, props.project)
}

const form = useForm({
  defaultValues: projectForm.value,
  validatorAdapter: yupValidator(),
  onSubmit: ({ value }) => {
    return props.saveFunction(value)
  }
})

const setAddresse = (e) => {
  const selectedValue = typeof e === 'string' ? e : e.target.value
  form.useStore((state) => state.values.address = selectedValue)
}
</script>
